import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { sanitizeEmailHtml } from "../lib/sanitizeHtml";
import {
  Megaphone, Plus, ChevronRight, ChevronLeft, MailOpen, MousePointerClick,
  Reply as ReplyIcon, Eye, Clock, Sparkles,
  CheckCircle2, Send, Users, Loader2, Trash2, FlaskConical, Pencil,
  CalendarCheck, Banknote,
} from "lucide-react";
import { Avatar, Badge, Button, EmptyState, Input, Modal, ModalFooter, Select, Skeleton, Textarea, Tooltip, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { RichEmailEditor } from "@/components/RichEmailEditor";
import { formatRelative, formatDateTime, formatMoney } from "@/lib/format";
import { emailDomainOf, formatSender, useEmailSending } from "@/lib/email-sending";
import { cn } from "@/lib/utils";

interface Campaign {
  id: string;
  name: string;
  status: string;
  /** 'email' or 'sms' — she blasts both [23:27]. */
  channel?: string;
  subject: string;
  body: string | null;
  html_body: string | null;
  from_name: string | null;
  from_email: string | null;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  replied_count: number;
  bounced_count: number;
  unsubscribed_count: number;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  /** "how many ppl actually booked after the advertising" — people, not visits. */
  booked_count?: number;
  booked_revenue_cents?: number;
}

interface Recipient {
  id: string;
  contact_id: string;
  status: string;
  sent_at: string | null;
  first_opened_at: string | null;
  open_count: number;
  first_clicked_at: string | null;
  click_count: number;
  replied_at: string | null;
  bounced_at: string | null;
  bounce_reason: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_avatar: string | null;
  /** The visit they booked after it went out, if they did. */
  booked?: { at: string; title: string; starts_at: string } | null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: "zinc",
  scheduled: "amber",
  sending: "blue",
  sent: "emerald",
  paused: "rose",
};

export function CampaignsPage() {
  const toast = useToast();
  const { id: routeId } = useParams<{ id?: string }>();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  /** When she starts from a suggestion, the composer opens already filled in. */
  const [seed, setSeed] = useState<{ brief: string; channel: "sms" | "email" } | null>(null);

  const load = () => {
    setLoading(true);
    api.get<{ campaigns: Campaign[] }>("/email/campaigns")
      .then((d) => setCampaigns(d.campaigns))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load campaigns"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (routeId) {
    return <CampaignDetail id={routeId} />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-linear-to-br from-violet-500 to-fuchsia-600 grid place-items-center shadow-lg shadow-violet-500/20">
          <Megaphone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-zinc-100">Campaigns</h1>
          <p className="text-xs text-zinc-500">Text and email blasts, tracked per client.</p>
        </div>
        <Button className="ml-auto bg-linear-to-br from-indigo-500 to-violet-600 border-0"
          onClick={() => setComposing(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New campaign
        </Button>
      </div>

      {composing && (
        <ComposeCampaign
          seedBrief={seed?.brief}
          seedChannel={seed?.channel}
          onClose={() => { setComposing(false); setSeed(null); }}
          onCreated={() => { setComposing(false); setSeed(null); load(); }}
        />
      )}

      {/* Stats summary */}
      <CampaignsSummary campaigns={campaigns} />

      {/* "have ai in it to help advertise ways" — three things worth sending
          THIS week, worked out from who is actually on her list. */}
      <CampaignIdeas onUse={(brief, channel) => { setSeed({ brief, channel }); setComposing(true); }} />

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyState icon={Megaphone} title="No campaigns yet"
        description="Send a text or an email to one of your audiences." />
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => <CampaignRow key={c.id} campaign={c} />)}
        </div>
      )}
    </div>
  );
}

interface CampaignIdea { title: string; who: string; angle: string; channel?: string }

/**
 * What is worth sending at all.
 *
 * Not marketing-blog filler: the assistant is given her real menu, her real
 * prices, what has actually been booked lately and how many clients have not
 * been in for two months, and asked for three things she could send this week.
 * Using one opens the composer with the idea already in the box.
 */
function CampaignIdeas({ onUse }: { onUse: (brief: string, channel: "sms" | "email") => void }) {
  const [ideas, setIdeas] = useState<CampaignIdea[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const ask = () => {
    setOpen(true);
    if (ideas || loading) return;
    setLoading(true);
    api.get<{ ideas: CampaignIdea[] }>("/ai/campaign-ideas")
      .then((d) => setIdeas(Array.isArray(d.ideas) ? d.ideas : []))
      .catch(() => setIdeas([]))
      .finally(() => setLoading(false));
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={ask}
        className="flex w-full items-center gap-2 rounded-xl border border-dashed border-indigo-500/40 bg-indigo-500/5 px-4 py-3 text-left hover:border-indigo-500/70 hover:bg-indigo-500/10"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-indigo-300" />
        <span className="text-sm font-medium text-zinc-100">What should I send this week?</span>
        <span className="hidden text-xs text-zinc-500 sm:inline">
          Three ideas from your own clients and your own menu
        </span>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-indigo-300" />
        <p className="text-sm font-semibold text-zinc-100">Worth sending this week</p>
        <button onClick={() => setOpen(false)} className="ml-auto text-xs text-zinc-500 hover:text-zinc-300">
          Hide
        </button>
      </div>
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : (ideas ?? []).length === 0 ? (
        <p className="text-sm text-zinc-400">
          No ideas came back just now. Try again in a moment, or write your own above.
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-3">
          {(ideas ?? []).map((idea, i) => (
            <li key={i} className="flex flex-col rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
              <p className="text-sm font-semibold text-zinc-100">{idea.title}</p>
              <p className="mt-0.5 text-[11px] uppercase tracking-wider text-zinc-500">{idea.who}</p>
              <p className="mt-1 flex-1 text-xs text-zinc-300">{idea.angle}</p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-2 self-start"
                onClick={() => onUse(`${idea.title}. ${idea.who}. ${idea.angle}`,
                  idea.channel === "email" ? "email" : "sms")}
              >
                Use this
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CampaignsSummary({ campaigns }: { campaigns: Campaign[] }) {
  const sent = campaigns.filter((c) => c.status === "sent");
  const totals = sent.reduce(
    (acc, c) => ({
      sent: acc.sent + c.sent_count,
      opened: acc.opened + c.opened_count,
      clicked: acc.clicked + c.clicked_count,
      replied: acc.replied + c.replied_count,
      booked: acc.booked + (c.booked_count ?? 0),
      money: acc.money + (c.booked_revenue_cents ?? 0),
    }),
    { sent: 0, opened: 0, clicked: 0, replied: 0, booked: 0, money: 0 }
  );
  const openRate = totals.sent ? Math.round((totals.opened / totals.sent) * 100) : 0;
  const clickRate = totals.sent ? Math.round((totals.clicked / totals.sent) * 100) : 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <StatTile icon={Send} label="Sent" value={totals.sent.toLocaleString()} accent="indigo" />
      <StatTile icon={MailOpen} label="Open rate" value={`${openRate}%`} sub={`${totals.opened.toLocaleString()} opens`} accent="emerald" />
      <StatTile icon={MousePointerClick} label="Click rate" value={`${clickRate}%`} sub={`${totals.clicked.toLocaleString()} clicks`} accent="violet" />
      <StatTile icon={ReplyIcon} label="Replies" value={totals.replied.toLocaleString()} accent="amber" />
      {/* The one at the end: of the people this went to, how many came in. */}
      <StatTile icon={CalendarCheck} label="Booked after" value={totals.booked.toLocaleString()}
        sub={totals.money > 0 ? `${formatMoney(totals.money)} taken` : "within 30 days"} accent="sky" />
    </div>
  );
}

function StatTile({
  icon: Icon, label, value, sub, accent,
}: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string; accent: string }) {
  const cls: Record<string, string> = {
    indigo: "from-indigo-500/20 to-indigo-500/0 text-indigo-300",
    emerald: "from-emerald-500/20 to-emerald-500/0 text-emerald-300",
    violet: "from-violet-500/20 to-violet-500/0 text-violet-300",
    amber: "from-amber-500/20 to-amber-500/0 text-amber-300",
    sky: "from-sky-500/20 to-sky-500/0 text-sky-300",
  };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 flex items-center gap-3">
      <div className={cn("h-9 w-9 rounded-lg bg-linear-to-br grid place-items-center", cls[accent])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="text-lg font-bold text-zinc-100">{value}</p>
        {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
      </div>
    </div>
  );
}

function CampaignRow({ campaign }: { campaign: Campaign }) {
  const openRate = campaign.sent_count ? Math.round((campaign.opened_count / campaign.sent_count) * 100) : 0;
  const clickRate = campaign.sent_count ? Math.round((campaign.clicked_count / campaign.sent_count) * 100) : 0;
  return (
    <Link
      to={`/campaigns/${campaign.id}`}
      className="block rounded-xl border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/60 hover:border-zinc-700 px-4 py-3 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-zinc-100 truncate">{campaign.name}</h3>
            <Badge color={STATUS_COLORS[campaign.status] ?? "zinc"} size="xs">{campaign.status}</Badge>
            <Badge color={campaign.channel === "sms" ? "violet" : "sky"} size="xs">
              {campaign.channel === "sms" ? "Text" : "Email"}
            </Badge>
          </div>
          <p className="text-xs text-zinc-400 truncate">
            {campaign.channel === "sms" ? (campaign.body ?? "") : campaign.subject}
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">
            {campaign.sent_at ? `Sent ${formatRelative(campaign.sent_at)}` :
             campaign.scheduled_at ? `Scheduled ${formatRelative(campaign.scheduled_at)}` :
             `Created ${formatRelative(campaign.created_at)}`}
            {" · "}
            <Users className="inline h-3 w-3 -mt-px" /> {campaign.total_recipients.toLocaleString()} recipients
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-right shrink-0">
          <Metric icon={MailOpen} value={`${openRate}%`} sub="opened" color="emerald" />
          <Metric icon={MousePointerClick} value={`${clickRate}%`} sub="clicked" color="violet" />
          <Metric icon={ReplyIcon} value={campaign.replied_count.toString()} sub="replies" color="amber" />
          <Metric icon={CalendarCheck} value={(campaign.booked_count ?? 0).toString()} sub="booked" color="sky" />
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-600 mt-1 shrink-0" />
      </div>
    </Link>
  );
}

function Metric({ icon: Icon, value, sub, color }: { icon: React.ComponentType<{ className?: string }>; value: string; sub: string; color: string }) {
  const text: Record<string, string> = {
    emerald: "text-emerald-300",
    violet: "text-violet-300",
    amber: "text-amber-300",
    sky: "text-sky-300",
  };
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="flex items-center gap-1">
        <Icon className={cn("h-3 w-3", text[color])} />
        <span className="text-sm font-semibold text-zinc-100">{value}</span>
      </div>
      <span className="text-[10px] text-zinc-500">{sub}</span>
    </div>
  );
}

// ─── Campaign detail page ─────────────────────────────────────────────────

function CampaignDetail({ id }: { id: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [previewIdx, setPreviewIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const d = await api.get<{ campaign: Campaign; recipients: Recipient[] }>(`/email/campaigns/${id}`);
      setCampaign(d.campaign); setRecipients(d.recipients);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load campaign");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setLoading(true); void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  /**
   * The wrapped email, straight from the api.
   *
   * Asked for again whenever the body or the chosen recipient changes, so
   * what is on screen is never a stale render of an edited draft.
   *
   * These three sit ABOVE the loading and not-found returns on purpose. Below
   * them the page rendered eight hooks while it was loading and eleven once it
   * had loaded, which is the one thing React will not have: every campaign
   * opened on "Something broke · Rendered more hooks than during the previous
   * render", so the list was as far as anybody ever got.
   */
  const [serverPreview, setServerPreview] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState(false);
  /* Derived here too, because the effect below needs it before the returns. */
  const previewContact = recipients[previewIdx] ?? null;
  useEffect(() => {
    if (!campaign?.html_body) { setServerPreview(null); return; }
    let dead = false;
    api.get<{ html: string }>(`/email/campaigns/${campaign.id}/preview`,
      previewContact?.contact_id ? { contact_id: previewContact.contact_id } : undefined)
      .then((d) => { if (!dead) setServerPreview(d.html); })
      .catch(() => { if (!dead) setServerPreview(null); });
    return () => { dead = true; };
  }, [campaign?.id, campaign?.html_body, campaign?.subject, previewContact?.contact_id]);

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }
  if (!campaign) return <p className="text-sm text-zinc-500">Campaign not found.</p>;

  const filtered = statusFilter === "all" ? recipients : recipients.filter((r) => r.status === statusFilter);

  const openRate = campaign.sent_count ? Math.round((campaign.opened_count / campaign.sent_count) * 100) : 0;
  const clickRate = campaign.sent_count ? Math.round((campaign.clicked_count / campaign.sent_count) * 100) : 0;
  const replyRate = campaign.sent_count ? Math.round((campaign.replied_count / campaign.sent_count) * 100) : 0;

  const isDraft = campaign.status === "draft" || campaign.status === "scheduled";
  const previewedSubject = renderClientTokens(campaign.subject, previewContact);
  const previewedText = renderClientTokens(campaign.body ?? "", previewContact);


  const sendNow = async () => {
    const ok = await confirm({
      title: `Send to ${campaign.total_recipients.toLocaleString()} recipients?`,
      description: "This goes out immediately through your connected ESP. Make sure the preview below looks right.",
      confirmLabel: "Send now",
    });
    if (!ok) return;
    setSending(true);
    try {
      const r = await api.post<{ sent_count: number; bounced_count: number; errors: unknown[] }>(`/email/campaigns/${id}/send`);
      toast.success(`Sent to ${r.sent_count}. ${r.bounced_count > 0 ? `${r.bounced_count} bounced.` : ""}`);
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post(`/email/campaigns/${id}/send-test`);
      toast.success("Test sent to you");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const discard = async () => {
    const ok = await confirm({
      title: "Discard this campaign?",
      description: "Permanently removes the draft and its recipient list. Can't be undone.",
      confirmLabel: "Discard",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/email/campaigns/${id}`);
      toast.success("Campaign discarded");
      navigate("/campaigns");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to discard");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/campaigns" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 text-zinc-400 hover:text-zinc-100">
          <ChevronLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-zinc-100 truncate">{campaign.name}</h1>
            <Badge color={STATUS_COLORS[campaign.status] ?? "zinc"} size="sm">{campaign.status}</Badge>
          </div>
          <p className="text-sm text-zinc-400 truncate">{campaign.subject}</p>
        </div>
      </div>

      {/* ─── Cockpit: actions for a draft, stats for a sent campaign ─── */}
      {isDraft ? (
        <div className="rounded-xl border border-indigo-500/30 bg-linear-to-br from-indigo-500/8 to-violet-500/5 p-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-linear-to-br from-indigo-500 to-violet-600 grid place-items-center shrink-0 shadow-lg shadow-indigo-500/30">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="text-sm text-zinc-200 leading-relaxed flex-1">
              <p className="font-semibold text-zinc-100">Ready to send</p>
              <p className="mt-1 text-zinc-400 text-xs">
                {campaign.total_recipients.toLocaleString()} {campaign.total_recipients === 1 ? "recipient" : "recipients"} will receive this email.
                Personalization tokens like <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{first_name}}"}</code>, <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{text_offers}}"}</code> (a link that turns offers by text on), <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{my_page}}"}</code>, or any custom field via <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{custom.field_name}}"}</code> are rendered per-recipient at send time.
                Send a test to yourself first.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Tooltip content={`Real send to ${campaign.total_recipients.toLocaleString()} recipients via your ESP`}>
              <Button
                variant="primary"
                onClick={() => void sendNow()}
                disabled={sending || testing || campaign.total_recipients === 0}
                className="bg-linear-to-br from-indigo-500 to-violet-600 hover:from-indigo-400 hover:to-violet-500 border-0"
              >
                {sending
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending…</>
                  : <><Send className="h-3.5 w-3.5 mr-1.5" />Send now</>}
              </Button>
            </Tooltip>
            {/* A draft you cannot change is a draft you have to delete and
                rewrite, which is what happened to a typo before this. */}
            <Tooltip content="Change the subject, the words or the pictures">
              <Button variant="secondary" onClick={() => setEditingBody(true)} disabled={sending || testing}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
              </Button>
            </Tooltip>
            <Tooltip content="Send one copy to yourself with personalization for the first recipient">
              <Button
                variant="secondary"
                onClick={() => void sendTest()}
                disabled={sending || testing}
              >
                {testing
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending test…</>
                  : <><FlaskConical className="h-3.5 w-3.5 mr-1.5" />Send test to me</>}
              </Button>
            </Tooltip>
            <span className="text-[10px] text-zinc-500 ml-auto">
              Have your AI edit the draft? Just ask in the right panel.
            </span>
            <Tooltip content="Discard this campaign permanently">
              <button
                onClick={() => void discard()}
                className="h-9 w-9 grid place-items-center text-zinc-500 hover:text-rose-300 hover:bg-zinc-900 rounded-lg"
                aria-label="Discard"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>
      ) : (
        <>
          {/* Big stats — only for sent campaigns */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <BigStat label="Recipients" value={campaign.total_recipients.toLocaleString()} icon={Users} color="zinc" />
            <BigStat label="Delivered" value={campaign.delivered_count.toLocaleString()} icon={CheckCircle2} color="emerald" sub={`${campaign.bounced_count} bounced`} />
            <BigStat label="Opened" value={`${openRate}%`} icon={MailOpen} color="indigo" sub={`${campaign.opened_count.toLocaleString()} opens`} />
            <BigStat label="Clicked" value={`${clickRate}%`} icon={MousePointerClick} color="violet" sub={`${campaign.clicked_count.toLocaleString()} clicks`} />
            <BigStat label="Replied" value={`${replyRate}%`} icon={ReplyIcon} color="amber" sub={`${campaign.replied_count.toLocaleString()} replies`} />
          </div>

          {/* "we need to track what went out , how many ppl and how many ppl
              actually booked after the advertising" — opens and clicks say it
              was read. This says it worked. */}
          <div className="grid grid-cols-2 gap-2">
            <BigStat
              label="Booked after this"
              value={(campaign.booked_count ?? 0).toLocaleString()}
              icon={CalendarCheck}
              color="sky"
              sub={`of ${campaign.sent_count.toLocaleString()} it reached, within 30 days`}
            />
            <BigStat
              label="Money from those visits"
              value={formatMoney(campaign.booked_revenue_cents ?? 0)}
              icon={Banknote}
              color="emerald"
              sub="taken at checkout, not what was booked"
            />
          </div>

          {/* AI take */}
          <div className="rounded-xl border border-indigo-500/30 bg-linear-to-br from-indigo-500/10 to-violet-500/5 p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-linear-to-br from-indigo-500 to-violet-600 grid place-items-center shrink-0 shadow-lg shadow-indigo-500/30">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="text-sm text-zinc-200 leading-relaxed">
              <p className="font-semibold text-zinc-100">AI take on this campaign</p>
              <p className="mt-1 text-zinc-300">{buildCampaignAiTake(campaign)}</p>
            </div>
          </div>
        </>
      )}

      {/* ─── Email body preview ─── */}
      {(campaign.html_body || campaign.body) && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-100">Preview</h3>
            {previewContact && recipients.length > 1 && (
              <select
                value={previewIdx}
                onChange={(e) => setPreviewIdx(Number(e.target.value))}
                className="ml-auto h-7 rounded-md bg-zinc-900 border border-zinc-800 text-[11px] text-zinc-300 px-1.5 max-w-[200px]"
              >
                {recipients.slice(0, 20).map((r, i) => (
                  <option key={r.id} value={i}>
                    Preview as {r.contact_name ?? r.contact_email ?? "recipient"}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="p-4 bg-zinc-950 border-b border-zinc-900">
            <CampaignSender campaign={campaign} onSaved={(c) => setCampaign(c)} />
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3 mb-1">Subject</p>
            <p className="text-sm font-semibold text-zinc-100">{previewedSubject}</p>
          </div>
          {/* The email exactly as it will arrive, built by the same code the
              send uses - logo, colour and all. A styled <div> here would
              flatter it: the CRM's own fonts and spacing are not what a mail
              client applies. The frame is sandboxed, so nothing in a pasted
              email can run or reach the rest of the screen. */}
          {campaign.html_body ? (
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={serverPreview ?? "<p style=\"font:14px Helvetica;padding:16px;color:#777\">Loading…</p>"}
              className="h-[480px] w-full border-0 bg-white"
            />
          ) : (
            <div className="p-5 bg-white text-zinc-900 max-h-[480px] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{previewedText}</pre>
            </div>
          )}
        </div>
      )}

      {editingBody && (
        <CampaignBodyModal
          campaign={campaign}
          onClose={() => setEditingBody(false)}
          onSaved={(c) => { setCampaign(c); setEditingBody(false); }}
        />
      )}

      {/* Recipients table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-100">Recipients ({recipients.length})</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ml-auto h-7 rounded-md bg-zinc-900 border border-zinc-800 text-[11px] text-zinc-300 px-1.5"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="replied">Replied</option>
            <option value="bounced">Bounced</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="sent">Sent (not opened)</option>
          </select>
        </div>
        <div className="divide-y divide-zinc-900">
          {filtered.map((r) => <RecipientRow key={r.id} recipient={r} />)}
          {filtered.length === 0 && <p className="p-6 text-center text-xs text-zinc-500">No recipients match this filter.</p>}
        </div>
      </div>
    </div>
  );
}

/** Client-side token renderer for the preview pane. Mirrors the backend
 *  renderer in routes/email.ts so what the user sees IS what the
 *  recipient gets. Keep them in sync if either changes. */
/**
 * The campaign's From, editable until the send starts.
 *
 * Campaigns inherit the workspace sender at creation, which is right by
 * default and wrong for anyone whose marketing identity differs from their
 * transactional one — the whole reason to keep a separate sending domain.
 * Editing here was previously impossible: the API accepted from_name /
 * from_email but nothing in the UI could set them.
 *
 * Reuses the shared sending config (same source as the inbox composer and
 * Settings) so the verified-domain rules are stated once, not restated.
 */
function CampaignSender({ campaign, onSaved }: { campaign: Campaign; onSaved: (c: Campaign) => void }) {
  const toast = useToast();
  const { config } = useEmailSending();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(campaign.from_name ?? "");
  const [email, setEmail] = useState(campaign.from_email ?? "");
  const [saving, setSaving] = useState(false);

  // Locked once the send begins — the stored sender is the historical
  // record of what recipients actually received.
  const locked = campaign.status !== "draft" && campaign.status !== "scheduled";

  const typedDomain = emailDomainOf(email.trim());
  const domainOk = !config?.known || !typedDomain || config.verified_domains.includes(typedDomain);

  // What a send would actually use right now. A blank field falls back to
  // the workspace sender rather than sending blank.
  const shown = formatSender(
    campaign.from_name ?? config?.effective.from_name ?? null,
    campaign.from_email ?? config?.effective.from ?? null,
  );

  const save = async () => {
    setSaving(true);
    try {
      const d = await api.patch<{ campaign: Campaign }>(`/email/campaigns/${campaign.id}`, {
        from_name: name.trim() || null,
        from_email: email.trim() || null,
      });
      onSaved(d.campaign);
      setEditing(false);
      toast.success("Sender updated");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the sender");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">From</p>
          {!locked && (
            <button
              type="button"
              onClick={() => { setName(campaign.from_name ?? ""); setEmail(campaign.from_email ?? ""); setEditing(true); }}
              className="text-[10px] font-medium text-indigo-300 hover:text-indigo-200"
            >
              Change
            </button>
          )}
        </div>
        <p className="text-sm text-zinc-200">
          {shown || <span className="text-zinc-500 italic">Using the workspace default sender</span>}
        </p>
        {!campaign.from_email && shown && (
          <p className="text-[10px] text-zinc-500 mt-0.5">Workspace default — change it to send this campaign from a different address.</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">From</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your business name" className="h-8 text-xs" maxLength={128} />
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={config?.effective.from ?? "hello@yourdomain.com"} className="h-8 text-xs" />
      </div>
      {!domainOk && (
        <p className="mt-1 text-[11px] text-amber-400">
          {typedDomain} isn't a verified sending domain — this campaign would fail to send.
        </p>
      )}
      {config?.known && config.verified_domains.length > 0 && (
        <p className="mt-1 text-[10px] text-zinc-500">
          Verified: {config.verified_domains.join(", ")} · leave blank to use the workspace default
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={save} loading={saving}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}

/**
 * Changing a draft after it is written.
 *
 * The same editor the campaign was written in, so a photo can be swapped or
 * a line fixed without deleting the campaign and starting again. Locked once
 * a send has begun, because what people already received cannot be edited.
 */
function CampaignBodyModal({
  campaign, onClose, onSaved,
}: {
  campaign: Campaign;
  onClose: () => void;
  onSaved: (c: Campaign) => void;
}) {
  const toast = useToast();
  const isEmail = (campaign.channel ?? "email") === "email";
  const [name, setName] = useState(campaign.name);
  const [subject, setSubject] = useState(campaign.subject ?? "");
  const [html, setHtml] = useState(campaign.html_body ?? "");
  const [body, setBody] = useState(campaign.body ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const d = await api.patch<{ campaign: Campaign }>(`/email/campaigns/${campaign.id}`, {
        name: name.trim() || campaign.name,
        subject: isEmail ? subject.trim() : "",
        body: body.trim(),
        ...(isEmail ? { html_body: html.trim() } : {}),
      });
      toast.success("Campaign updated");
      onSaved(d.campaign);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Edit campaign" size="lg">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs text-zinc-400">What's it called? (only you see this)</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        {isEmail && (
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" />
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Message</label>
          {isEmail ? (
            <RichEmailEditor
              value={html}
              onChange={(nextHtml, nextText) => { setHtml(nextHtml); setBody(nextText); }}
              minHeight="18rem"
            />
          ) : (
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} />
          )}
          <p className="mt-1 text-[11px] text-zinc-500">
            {"Tokens like {{first_name}} and {{text_offers}} still work. "}
            Your logo and colour are added around it when it goes out.
          </p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} disabled={!body.trim()} onClick={() => void save()}>Save changes</Button>
      </ModalFooter>
    </Modal>
  );
}

function renderClientTokens(input: string, recipient: Recipient | null): string {
  if (!input) return "";
  const fullName = recipient?.contact_name ?? "";
  const first = fullName ? fullName.split(/\s+/)[0]! : "there";
  const last = fullName.split(/\s+/).slice(1).join(" ") || "";
  return input
    .replace(/\{\{\s*first_name\s*\}\}/gi, first)
    .replace(/\{\{\s*last_name\s*\}\}/gi, last)
    .replace(/\{\{\s*full_name\s*\}\}/gi, fullName || (recipient?.contact_email ?? "there"))
    .replace(/\{\{\s*email\s*\}\}/gi, recipient?.contact_email ?? "")
    .replace(/\{\{\s*company\s*\}\}/gi, "")
    // A personal link, different for every reader, so the preview shows what
    // it is rather than one person's real link.
    .replace(/\{\{\s*my_page\s*\}\}/gi, "[their own page]")
    .replace(/\{\{\s*text_offers\s*\}\}/gi, "[their own page, offers]")
    // {{custom.<key>}} — the real value is merged per-recipient at send time;
    // the preview can't know it, so show a readable placeholder rather than
    // leaving the raw token visible.
    .replace(/\{\{\s*custom\.([a-z0-9_]+)\s*\}\}/gi, (_m: string, key: string) => `[${key}]`);
}

function BigStat({ label, value, icon: Icon, color, sub }: { label: string; value: string; icon: React.ComponentType<{ className?: string }>; color: string; sub?: string }) {
  const bg: Record<string, string> = {
    zinc: "bg-zinc-800/40",
    emerald: "bg-emerald-500/10",
    indigo: "bg-indigo-500/10",
    violet: "bg-violet-500/10",
    amber: "bg-amber-500/10",
  };
  const text: Record<string, string> = {
    zinc: "text-zinc-300",
    emerald: "text-emerald-300",
    indigo: "text-indigo-300",
    violet: "text-violet-300",
    amber: "text-amber-300",
  };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("h-7 w-7 rounded-md grid place-items-center", bg[color])}>
          <Icon className={cn("h-3.5 w-3.5", text[color])} />
        </div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-zinc-100">{value}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function RecipientRow({ recipient }: { recipient: Recipient }) {
  const statusIcon = () => {
    if (recipient.bounced_at) return <Badge color="rose" size="xs">Bounced</Badge>;
    if (recipient.replied_at) return <Badge color="amber" size="xs">Replied</Badge>;
    if (recipient.first_clicked_at) return <Badge color="violet" size="xs">Clicked</Badge>;
    if (recipient.first_opened_at) return <Badge color="emerald" size="xs">Opened</Badge>;
    if (recipient.sent_at) return <Badge color="zinc" size="xs">Sent</Badge>;
    return <Badge color="zinc" size="xs">{recipient.status}</Badge>;
  };

  return (
    <Link
      to={recipient.contact_id ? `/contacts/${recipient.contact_id}` : "#"}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-900/40"
    >
      <Avatar name={recipient.contact_name} email={recipient.contact_email} src={recipient.contact_avatar} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-zinc-100 truncate">{recipient.contact_name ?? recipient.contact_email}</p>
        <p className="text-[11px] text-zinc-500 truncate">{recipient.contact_email}</p>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-[11px] text-zinc-500">
        {recipient.open_count > 0 && <span title="Opens"><Eye className="inline h-3 w-3" /> {recipient.open_count}</span>}
        {recipient.click_count > 0 && <span title="Clicks"><MousePointerClick className="inline h-3 w-3" /> {recipient.click_count}</span>}
        {recipient.sent_at && <span>{formatRelative(recipient.sent_at)}</span>}
      </div>
      {/* The point of the whole list: who came in afterwards. */}
      {recipient.booked && (
        <Badge color="sky" size="xs">
          Booked {formatRelative(recipient.booked.at)}
        </Badge>
      )}
      {statusIcon()}
    </Link>
  );
}

function buildCampaignAiTake(c: Campaign): string {
  const pct = (n: number) => (c.sent_count ? (n / c.sent_count) * 100 : 0);
  const openRate = pct(c.opened_count);
  const clickRate = pct(c.clicked_count);
  const bounceRate = pct(c.bounced_count);
  const booked = c.booked_count ?? 0;

  const parts: string[] = [];

  /* Bookings first, always. Everything else on this page is a proxy for it,
     and a campaign that filled the diary is a good campaign whatever its
     open rate did. */
  if (c.sent_count === 0) {
    parts.push("Nothing has gone out yet.");
  } else if (booked > 0) {
    const rate = (booked / c.sent_count) * 100;
    parts.push(
      `${booked} of the ${c.sent_count.toLocaleString()} people this reached booked a visit within 30 days (${rate.toFixed(1)}%).`,
    );
    if ((c.booked_revenue_cents ?? 0) > 0) {
      parts.push(`Those visits have taken ${formatMoney(c.booked_revenue_cents ?? 0)} so far.`);
    }
  } else {
    parts.push("Nobody has booked from this one yet. Bookings are counted for 30 days after it went out, so it may be early.");
  }

  if (openRate >= 35) parts.push(`${openRate.toFixed(0)}% opened it, which is a strong subject line.`);
  else if (openRate >= 20) parts.push(`${openRate.toFixed(0)}% opened it.`);
  else if (c.sent_count > 0) parts.push(`${openRate.toFixed(0)}% opened it, so the subject line is worth changing next time.`);

  if (clickRate > 0) parts.push(`${clickRate.toFixed(0)}% clicked through.`);
  if (c.replied_count > 0) parts.push(`${c.replied_count} replied, and a reply is a conversation worth having.`);
  if (bounceRate >= 5) parts.push(`${bounceRate.toFixed(0)}% bounced, which usually means old addresses worth cleaning out.`);
  if (c.unsubscribed_count > 0) parts.push(`${c.unsubscribed_count} opted out.`);

  return parts.join(" ");
}


// ─── Composer ──────────────────────────────────────────────────────────────
//
// [23:27] "We can either do text campaigns, or we can do email campaigns,
// which I've done them. We're not as aggressive as we need to be, but I want
// to get there." So the channel is the first choice, not a buried setting,
// and the audience comes from the same saved lists the nurses print.

function ComposeCampaign({ onClose, onCreated, seedBrief, seedChannel }: {
  onClose: () => void;
  onCreated: () => void;
  /** Pre-filled when she started from one of the suggested ideas. */
  seedBrief?: string;
  seedChannel?: "sms" | "email";
}) {
  const toast = useToast();
  const [channel, setChannel] = useState<"sms" | "email">(seedChannel ?? "sms");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  /** The designed version. Plain text is kept alongside it as the fallback. */
  const [html, setHtml] = useState("");
  const [listId, setListId] = useState("");
  const [lists, setLists] = useState<{ id: string; name: string; contact_count?: number }[]>([]);
  const [saving, setSaving] = useState(false);
  /** "have ai in it to help advertise ways" — she says what it is about, it
   *  writes the first draft. Nothing sends; it only fills the boxes. */
  const [brief, setBrief] = useState(seedBrief ?? "");
  const [writing, setWriting] = useState(false);
  /* "Or our photos for text campaigns or email?" — the same shelf the email
     editor picks from. A picture makes the text an MMS, which costs more per
     message, so it is never attached by accident. */
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [pictures, setPictures] = useState<{ id: string; url: string; name: string }[]>([]);

  const writeItForMe = async () => {
    if (!brief.trim()) { toast.error("Say what the campaign is about first"); return; }
    setWriting(true);
    try {
      const audience = lists.find((l) => l.id === listId)?.name;
      const d = await api.post<{ subject?: string; body?: string }>("/ai/campaign", {
        channel, brief: brief.trim(), ...(audience ? { audience } : {}),
      });
      if (channel === "email") {
        if (d.subject) setSubject(d.subject);
        const written = d.body ?? "";
        setHtml(written);
        setBody(written.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim());
      } else {
        setBody((d.body ?? "").trim());
      }
      if (!name.trim() && brief.trim()) setName(brief.trim().slice(0, 60));
      toast.success("Drafted. Read it over and change anything you like");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't write that");
    } finally { setWriting(false); }
  };

  useEffect(() => {
    api.get<{ lists: typeof lists }>("/lists").then((d) => setLists(d.lists)).catch(() => {});
    api.get<{ pictures: typeof pictures }>("/brand/pictures")
      .then((d) => setPictures(d.pictures ?? [])).catch(() => {});
  }, []);

  // A text is billed per 160-character segment, so show the count while typing.
  // Count what actually goes out: the send adds her name and the STOP line
  // when they're missing (api lib/campaign-send.ts smsMarketingBody).
  const smsOut = (() => {
    let out = body.trim();
    if (!/heavenly\s*hydration/i.test(out)) out = `Heavenly Hydration (text line): ${out}`;
    if (!/\bstop\b/i.test(out)) out = `${out}${/[.!?]$/.test(out) ? "" : "."} Reply STOP to opt out.`;
    return out;
  })();
  const segments = Math.max(1, Math.ceil((channel === "sms" ? smsOut.length : body.length) / 160));
  /* Only an estimate: not everyone on a list has ticked offers by text, and
     the real charge is whatever the carrier bills for each message. */
  const audienceSize = lists.find((l) => l.id === listId)?.contact_count ?? 0;

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/email/campaigns", {
        name: name.trim() || "Untitled campaign",
        channel,
        subject: channel === "email" ? subject.trim() : "",
        // Both: the HTML is what nearly everyone sees, the text is the
        // fallback for a client that refuses HTML.
        body: body.trim(),
        html_body: channel === "email" ? html.trim() : undefined,
        list_id: listId || undefined,
        ...(channel === "sms" && mediaUrl ? { media_url: mediaUrl } : {}),
      });
      toast.success("Saved as a draft — review it, then send when you're ready");
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't create that campaign");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="New campaign">
      <div className="space-y-3">
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/50 p-0.5">
          {([["sms", "Text message"], ["email", "Email"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => { setChannel(k); if (k === "email") setMediaUrl(null); }}
              className={cn("flex-1 rounded-md px-3 py-1.5 text-sm",
                channel === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200")}>
              {label}
            </button>
          ))}
        </div>

        {/* Her own words in, a first draft out. It never invents a price: her
            real menu goes to the assistant along with the question. */}
        <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3">
          <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-indigo-300">
            <Sparkles className="h-3.5 w-3.5" /> Write it for me
          </label>
          <Textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={2}
            placeholder="e.g. hydration slots free Thursday and Friday, for people we have not seen since the summer"
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => void writeItForMe()} loading={writing} disabled={writing}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {channel === "sms" ? "Write the text" : "Write the email"}
            </Button>
            <span className="text-[11px] text-zinc-500">It fills the boxes below. Nothing is sent.</span>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs text-zinc-400">What's it called? (only you see this)</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. August hydration promo" />
        </div>

        <div>
          <label className="mb-1 block text-xs text-zinc-400">Who gets it</label>
          <Select value={listId} onChange={(e) => setListId(e.target.value)}>
            <option value="">Choose an audience…</option>
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
          <p className="mt-1 text-[11px] text-zinc-500">
            Audiences are the saved client lists under Clients → Audiences.
            {channel === "sms" && " Texts only go to clients in it who ticked \"also text me offers\" and haven't replied STOP."}
          </p>
        </div>

        {channel === "email" && (
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" />
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs text-zinc-400">Message</label>
          {/* "The email marketing is designable? Like decorative up load
              photos logos stuff like that" - so an email is written in the
              same editor as any other email here: headings, photos dropped
              straight in, links and buttons. A text has none of that, so it
              keeps the plain box and its character count. */}
          {channel === "email" ? (
            <>
              <RichEmailEditor
                value={html}
                onChange={(nextHtml, nextText) => { setHtml(nextHtml); setBody(nextText); }}
                placeholder="Hi {{first_name}},"
                minHeight="16rem"
              />
              <p className="mt-1 text-[11px] text-zinc-500">
                Drag a photo straight in, or paste one. Your logo and colour are added
                around it automatically - set them in Settings.
              </p>
            </>
          ) : (
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
              placeholder="Hi {{first_name}}, we've got hydration slots open this week. Book at app.heavenlyhydrationli.com/booking" />
          )}
          <p className="mt-1 text-[11px] text-zinc-500">
            {"Use {{first_name}} to personalise, {{text_offers}} for a link that lets them turn offers by text on, or {{my_page}} for their own page. "}
            {channel === "sms"
              ? `${smsOut.length} characters including your business name and the STOP line · ${segments} text${segments === 1 ? "" : "s"} per person`
              : "Clients who have opted out are skipped automatically."}
          </p>
        </div>

        {/* "Or our photos for text campaigns or email?" — her own shelf, and
            the price said before she sends rather than after. */}
        {channel === "sms" && (
          <div>
            <label className="mb-1 block text-xs text-zinc-400">A picture with it (optional)</label>
            {pictures.length === 0 ? (
              <p className="text-[11px] text-zinc-500">
                No pictures saved yet. Upload them in Settings under Your brand and they show up here.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setMediaUrl(null)}
                  className={cn("h-16 w-16 rounded-lg border text-[11px] text-zinc-400",
                    mediaUrl === null ? "border-indigo-400 bg-indigo-500/10 text-indigo-300" : "border-zinc-700 hover:border-zinc-500")}
                >
                  No picture
                </button>
                {pictures.map((pic) => (
                  <button
                    key={pic.id}
                    type="button"
                    onClick={() => setMediaUrl(pic.url)}
                    title={pic.name}
                    className={cn("h-16 w-16 overflow-hidden rounded-lg border",
                      mediaUrl === pic.url ? "border-indigo-400 ring-2 ring-indigo-400/40" : "border-zinc-700 hover:border-zinc-500")}
                  >
                    <img src={pic.url} alt={pic.name} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {mediaUrl && (
              <p className="mt-1.5 text-[11px] text-amber-300/90">
                A picture sends this as a multimedia text, which costs roughly twice as much
                per person as a plain one (about 4 cents each rather than under 2).
                {audienceSize > 0 && (
                  <> For {audienceSize} {audienceSize === 1 ? "person" : "people"} that is
                    roughly {formatMoney(Math.round(audienceSize * 4))} instead
                    of {formatMoney(Math.round(audienceSize * 1.7 * segments))}.</>
                )}
              </p>
            )}
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={create} disabled={saving || !body.trim() || !listId}>Save as draft</Button>
      </ModalFooter>
    </Modal>
  );
}
